import * as THREE from "three";
import { inked } from "../toon";
import { closeSeam, smooth } from "./geom";
import type { SkaterLook } from "./look";
import { bottomsCloth } from "./materials";

// The realistic rider's shaped torso (#75, `RiderBuild.torso: "shaped"`),
// torso space (up +y, chest toward -x, arms at ±z). Where the toy's capsule
// rounds off under the shoulders and leaves the arms hanging beside it, this
// runs from the waist out to the shoulder points, where the arms pivot, then
// slopes up into the neck, so the arms grow out of the body.

/** Heights up a loft with the half-width across (z) and half-depth (x) there. */
type Keys = [y: number, w: number, d: number][];

/** Heights up the torso with the half-width across (z) and half-depth (x) there. */
const KEYS: Keys = [
  [-0.03, 0.15, 0.105], // down into the pelvis
  [0.08, 0.14, 0.1], // the waist
  [0.22, 0.158, 0.112], // the ribs
  [0.33, 0.18, 0.118], // the chest
  [0.415, 0.212, 0.104], // the shoulder points, just past the arms' pivots
  [0.47, 0.168, 0.084], // over the shoulders
  [0.52, 0.09, 0.064], // the trapezius, up into the neck
  [0.54, 0.05, 0.04],
];
const BOTTOM = KEYS[0]![0];
const TOP = KEYS[KEYS.length - 1]![0];

/**
 * The shaped pelvis (hips space: the hip joints at y 0, z ±FOOT_Z / 2),
 * top to bottom. It tucks up inside the trunk's open bottom, stands a few
 * millimeters proud of it as a waistband, flares out over the thighs' tops
 * so the hip's outline runs straight on down the thigh, and rounds under
 * between the legs, so the pelvis and thighs read as one body, not a box on
 * two tubes.
 */
const PELVIS: Keys = [
  [0.1, 0.12, 0.085], // up inside the trunk
  [0.085, 0.14, 0.1],
  [0.07, 0.153, 0.109], // the waistband, just proud of the trunk
  [0.02, 0.16, 0.11],
  [-0.025, 0.18, 0.104], // over the thighs' tops, out to their outer side
  [-0.06, 0.172, 0.088],
  [-0.09, 0.125, 0.055], // rounding under, between the legs
  [-0.105, 0.05, 0.024],
];
/** Where a narrow build's pelvis goes from its waist's width (top) to full width over the hips (bottom). */
const HIP_FLARE: [number, number] = [0.02, -0.025];
/** Where the waistband is: a belt goes round here. */
export const WAIST_Y: [number, number] = [0.045, 0.08];
/** Cut-offs sit baggier than pants: across over the shorts legs' tops, and front to back. */
const BAGGY = { w: 1.08, d: 1.1 };
/** A squarish cross-section (a superellipse): a chest and back, not a tube. */
const SQUARE = 2.6;

/** The half-width and half-depth at height y, eased between the keys (listed bottom to top or top to bottom). */
function sizeAt(keys: Keys, y: number): { w: number; d: number } {
  const up = keys[0]![0] < keys[keys.length - 1]![0];
  const first = keys[0]!;
  const last = keys[keys.length - 1]!;
  if (up ? y <= first[0] : y >= first[0]) return { w: first[1], d: first[2] };
  for (let i = 1; i < keys.length; i++) {
    const [y1, w1, d1] = keys[i]!;
    if (up ? y <= y1 : y >= y1) {
      const [y0, w0, d0] = keys[i - 1]!;
      const t = smooth((y - y0) / (y1 - y0));
      return { w: w0 + (w1 - w0) * t, d: d0 + (d1 - d0) * t };
    }
  }
  return { w: last[1], d: last[2] };
}

/** The half-width and half-depth at height y, eased between the keys. */
export function trunkSize(y: number): { w: number; d: number } {
  return sizeAt(KEYS, y);
}

/**
 * The pelvis's half-width and half-depth at height y (hips space), for pants
 * or baggier cut-offs. A narrow build (`width` under 1) narrows the waist to
 * meet its trunk, but not the hips: the hip joints don't move.
 */
export function pelvisSize(y: number, baggy = false, width = 1): { w: number; d: number } {
  const { w, d } = sizeAt(PELVIS, y);
  const narrow = width + (1 - width) * smooth(Math.min(1, Math.max(0, (HIP_FLARE[0] - y) / (HIP_FLARE[0] - HIP_FLARE[1]))));
  return baggy ? { w: w * BAGGY.w * narrow, d: d * BAGGY.d } : { w: w * narrow, d };
}

/**
 * Where the pelvis's front (toward -x) or back (+x) is at height y and z
 * across (hips space): where denim details sit.
 */
export function pelvisSurface(y: number, z: number, back: boolean, width = 1, baggy = false): number {
  const { w, d } = pelvisSize(y, baggy, width);
  const across = Math.min(1, Math.abs(z) / w);
  return (back ? d : -d) * (1 - across ** SQUARE) ** (1 / SQUARE);
}

/** Where the trunk's chest is (its x, toward -x) at height y and z across: where chest details sit. */
export function trunkFront(y: number, z: number, width = 1): number {
  const { w, d } = trunkSize(y);
  const across = Math.min(1, Math.abs(z) / (w * width));
  return -d * (1 - across ** SQUARE) ** (1 / SQUARE);
}

/** The point at angle a round the section (0 is the chest, -x; pi/2 is +z) at height y. */
function sectionPoint(size: (y: number) => { w: number; d: number }, a: number, y: number, width: number): THREE.Vector3 {
  const { w, d } = size(y);
  const e = 2 / SQUARE;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const x = -d * Math.sign(c) * Math.abs(c) ** e;
  const z = w * width * Math.sign(s) * Math.abs(s) ** e;
  return new THREE.Vector3(x, y, z);
}

/** How a loft ends: open, or capped at a point this far past its last ring. */
interface Caps {
  top?: number;
  bottom?: number;
}

/** A grid over (angle, height) on the trunk, closed over the top; non-seamed round, so normals are smooth. */
function sheet(a0: number, a1: number, y0: number, y1: number, width: number, out: number, closed: boolean): THREE.BufferGeometry {
  return loft(trunkSize, a0, a1, y0, y1, width, out, closed, closed ? { top: 0.005 } : {});
}

/**
 * A grid over (angle, height) through `size`'s sections, from y0 up to y1,
 * round when `closed` (its last column repeats the first at u 1, so the
 * weave wraps once round; the seam's normals are averaged, so it's smooth),
 * with any caps.
 */
function loft(
  size: (y: number) => { w: number; d: number },
  a0: number,
  a1: number,
  y0: number,
  y1: number,
  width: number,
  out: number,
  closed: boolean,
  caps: Caps,
  dense = 1,
): THREE.BufferGeometry {
  const AROUND = (closed ? 40 : 14) * dense;
  const UP = 24 * dense;
  const pos: number[] = [];
  const uv: number[] = [];
  const cols = AROUND + 1;
  for (let i = 0; i < cols; i++) {
    const a = a0 + ((a1 - a0) * i) / AROUND;
    for (let j = 0; j <= UP; j++) {
      const y = y0 + ((y1 - y0) * j) / UP;
      const p = sectionPoint(size, a, y, width);
      pos.push(p.x, p.y, p.z);
      uv.push(i / AROUND, j / UP);
    }
  }
  const index: number[] = [];
  for (let i = 0; i < AROUND; i++) {
    for (let j = 0; j < UP; j++) {
      const a = i * (UP + 1) + j;
      const b = (i + 1) * (UP + 1) + j;
      index.push(a, b, a + 1, b, b + 1, a + 1);
    }
  }
  if (caps.top !== undefined) {
    // A cap over the top (the trunk's, under the neck).
    const top = pos.length / 3;
    pos.push(0, y1 + caps.top, 0);
    uv.push(0.5, 1);
    for (let i = 0; i < AROUND; i++) index.push(i * (UP + 1) + UP, (i + 1) * (UP + 1) + UP, top);
  }
  if (caps.bottom !== undefined) {
    // A cap under the bottom (the pelvis's, between the legs), wound to face down.
    const bottom = pos.length / 3;
    pos.push(0, y0 - caps.bottom, 0);
    uv.push(0.5, 0);
    for (let i = 0; i < AROUND; i++) index.push((i + 1) * (UP + 1), i * (UP + 1), bottom);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  if (closed) closeSeam(geo, UP + 1, AROUND);
  else geo.computeVertexNormals();
  if (out) {
    // Stand off along the normal: over the steep slope into the neck a sideways push isn't enough.
    const P = geo.getAttribute("position");
    const N = geo.getAttribute("normal");
    for (let i = 0; i < P.count; i++) P.setXYZ(i, P.getX(i) + N.getX(i) * out, P.getY(i) + N.getY(i) * out, P.getZ(i) + N.getZ(i) * out);
  }
  return geo;
}

/** The torso in the top's cloth: one mesh, waist to neck. `width` narrows it for a narrow build. */
export function trunkGeometry(width = 1): THREE.BufferGeometry {
  return sheet(0, Math.PI * 2, BOTTOM, TOP, width, 0, true);
}

/**
 * The trunk's whole surface `out` proud of it, `dense` times finer than the
 * trunk, for a garment to be cut from (a tank top, `outfit-natural.ts`).
 */
export function trunkShell(width: number, out: number, dense: number): THREE.BufferGeometry {
  return loft(trunkSize, 0, Math.PI * 2, BOTTOM, TOP, width, out, true, {}, dense);
}

/**
 * The shaped torso, `width` across (a narrow build is under 1): in the top's
 * cloth, or bare skin under a tank top.
 */
export function buildTrunk(mat: THREE.Material, width: number): THREE.Mesh {
  const trunk = inked(trunkGeometry(width), mat);
  trunk.name = "trunk";
  return trunk;
}

/** The pelvis: one closed mesh from up inside the trunk to between the legs (hips space). */
export function pelvisGeometry(width = 1, baggy = false): THREE.BufferGeometry {
  const top = PELVIS[0]![0];
  const bottom = PELVIS[PELVIS.length - 1]![0];
  return loft((y) => pelvisSize(y, baggy, width), 0, Math.PI * 2, bottom, top, 1, 0, true, { top: 0.004, bottom: 0.006 });
}

/** The shaped pelvis in the bottoms' cloth (denim for cut-offs, stretch for leggings), `width` across; cut-offs and baggy pants (#234) cut looser. */
export function buildPelvis(bottoms: SkaterLook["bottoms"], width: number): THREE.Mesh {
  const loose = bottoms.style === "cutoffs" || bottoms.style === "baggy";
  const pelvis = inked(pelvisGeometry(width, loose), bottomsCloth(bottoms));
  pelvis.name = "pelvis";
  return pelvis;
}

/** A belt round the shaped pelvis's waistband (hips space), a little proud of it. */
export function beltGeometry(width = 1): THREE.BufferGeometry {
  return loft((y) => pelvisSize(y, false, width), 0, Math.PI * 2, WAIST_Y[0], WAIST_Y[1], 1, 0.006, true, {});
}
